/**
 * @file UsersTable.jsx
 * @description UsersTable component for displaying and managing users table.
 * @component UsersTable
 * @exports UsersTable - Main component for displaying and managing users table.
 */

import React, { useState, useEffect } from "react";
import Menu from "@mui/material/Menu";
import { Alert } from "@mui/material";
import Table from "@mui/material/Table";
import MenuItem from "@mui/material/MenuItem";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import IconButton from "@mui/material/IconButton";
import { MoreVert } from "@mui/icons-material";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import LinearProgress from '@mui/material/LinearProgress';
import TablePagination from "@mui/material/TablePagination";
import { getAgentIdentity } from "../../../services/ivrModule/acsServices.js";
import { editUser } from "../../../services/adminModule/users/userServices.js";
import { refreshAgentToken } from "../../../services/ivrModule/acsServices.js";
import { SIZES, SORT_ORDERS, ALIGN, HTML_TAGS, STATUS } from "../../../common/constants/commonConstants.js";
import { USER_TABLE_COLUMN_KEYS, USER_MESSAGES, USER_ERRORS, USER_STRINGS } from "../../../common/constants/adminModule/userConstants.js";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";

const UsersTable = ({
    page,
    order,
    setPage,
    orderBy,
    isLoading,
    loadUsers,
    users,
    rowsPerPage,
    columns,
    onEdit,
    setRowsPerPage,
    onDelete,
    handleRequestSort,
    filteredUsersLength,
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [isUpdating, setIsUpdating] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const startLoading = () => setIsUpdating((prev) => prev + 1);
	const stopLoading = () => setIsUpdating((prev) => Math.max(prev - 1, 0));
    const showProgress = isUpdating > 0;

    const handleMenuOpen = (event, user) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedUser(null);
    };

    const handleRefreshToken = async (selectedACSIdentity) => {
        if (!selectedACSIdentity) {
            console.warn("No ACS Identity provided for token refresh.");
            return;
        }
        try {
            startLoading();
            const data = await refreshAgentToken(selectedACSIdentity);
            if (!data?.token || !data?.expiresOn) {
                console.error("Invalid token refresh response:", data);
                setSuccessMessage(USER_ERRORS.USER_TOKEN_REFRESH_ERROR);
                return;
            }
            const acsIdentity = localStorage.getItem("acsIdentity");
            if (acsIdentity === selectedACSIdentity) {
                localStorage.setItem("ACSIdentityToken", data.token);
                localStorage.setItem("ACSIdentityExpiration", data.expiresOn);
            }
            setSuccessMessage(USER_MESSAGES.USER_TOKEN_REFRESHED);
            await loadUsers();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (err) {
            console.error("Token refresh failed", err);
            setSuccessMessage(USER_ERRORS.USER_TOKEN_REFRESH_ERROR);
            setTimeout(() => setSuccessMessage(""), 3000);
        } finally {
            stopLoading();
        }
    };

    const handleCreateACSIdentity = async (selectedUser) => {
        if (!selectedUser) return;
        try {
            startLoading();
            const acsInfo = await getAgentIdentity();
            if (!acsInfo || !acsInfo.userId || !acsInfo.token) {
                console.warn("ACS Identity not available. Failed to update user.");
                return;
            }
            const expiresOn = new Date(acsInfo.expiresOn);
            const formattedExpiresOn = `${expiresOn.getFullYear()}-${String(expiresOn.getMonth() + 1).padStart(2, "0")}-${String(
                expiresOn.getDate()
            ).padStart(2, "0")} ${String(expiresOn.getHours()).padStart(2, "0")}:${String(
                expiresOn.getMinutes()
            ).padStart(2, "0")}:${String(expiresOn.getSeconds()).padStart(2, "0")}.${String(
                expiresOn.getMilliseconds()
            ).padStart(3, "0")}`;
            let workerid = selectedUser.workerid;
            if (!workerid || workerid.trim() === "") {
                const acsIdTokenPart = acsInfo.userId.slice(-4);
                const userLastName = selectedUser.lastname
                    ? selectedUser.lastname.toLowerCase().replace(/\s+/g, "")
                    : "user";
                workerid = `wkr-${userLastName}-${acsIdTokenPart}`;
            }
            const userToSave = {
                ...selectedUser,
                ACSIdentity: acsInfo.userId,
                ACSIdentityToken: acsInfo.token,
                ACSIdentityExpiration: formattedExpiresOn,
                workerid: workerid,
            };
            await editUser(userToSave);
            setSuccessMessage(USER_MESSAGES.USER_ACS_IDENTITY_CREATED);
            await loadUsers();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("ACS Identity creationfailed", error);
            setSuccessMessage(USER_ERRORS.USER_ACS_IDENTITY_ERROR);
            setTimeout(() => setSuccessMessage(""), 3000);
        } finally {
            stopLoading();
        }
    };

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(""), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    return (
        <>
            {successMessage && (
                <>
                    <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                        {successMessage}
                    </Alert>
                </>
            )}
            {showProgress && (
				<>
					<ProgressOverlay />
				</>
			)}
            <TableContainer sx={{ mt: 2 }}>
                <Table size={SIZES.SMALL}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    sortDirection={orderBy === column.id ? order : false}
                                    sx={{
                                        width:
                                            column.id === USER_TABLE_COLUMN_KEYS.ACTIONS ? 50 :
                                            column.id === USER_TABLE_COLUMN_KEYS.NAME ? 175 :
                                            column.id === USER_TABLE_COLUMN_KEYS.ROLE_NAME ? 125 :
                                            column.id === USER_TABLE_COLUMN_KEYS.ACS_IDENTITY ? 600 : 150,
                                    }}
                                >
                                    {column.isSortable ? (
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : SORT_ORDERS.ASCENDING}
                                            onClick={() =>
                                                handleRequestSort(column.id)
                                            }
                                        >
                                            {column.label}
                                        </TableSortLabel>
                                    ) : column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    <LinearProgress />
                                </TableCell>
                            </TableRow>
                        ) : users.length > 0 ? users.map((user) => (
                            <TableRow hover tabIndex={-1} key={user.id}>
                                <TableCell>
                                    <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                                        <MoreVert />
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    {user.name}
                                </TableCell>
                                <TableCell>
                                    {user.rolename}
                                </TableCell>
                                <TableCell>
                                    {user.ACSIdentity}
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} align={ALIGN.CENTER}>
                                    {USER_MESSAGES.NO_USERS}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    component={HTML_TAGS.DIV}
                    count={filteredUsersLength}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setPage(0);
                        setRowsPerPage(parseInt(event.target.value, 10));
                    }}
                />
            </TableContainer>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {selectedUser && (!selectedUser.ACSIdentity || selectedUser.ACSIdentity.trim() === "") ? (
                    <MenuItem
                        onClick={() => {
                            handleCreateACSIdentity(selectedUser);
                            handleMenuClose();
                        }}
                    >
                        {USER_STRINGS.CREATE_ACS_IDENTITY}
                    </MenuItem>
                ) : (
                    <MenuItem
                        onClick={() => {
                            handleRefreshToken(selectedUser.ACSIdentity);
                            handleMenuClose();
                        }}
                    >
                        {USER_STRINGS.REFRESH_TOKEN}
                    </MenuItem>
                )}
                <MenuItem
                    onClick={() => {
                        onEdit(selectedUser);
                        handleMenuClose();
                    }}
                >
                    {USER_STRINGS.EDIT_USER}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onDelete(selectedUser.id);
                        handleMenuClose();
                    }}
                >
                    {USER_STRINGS.DELETE_USER}
                </MenuItem>
            </Menu>
        </>
    )
};

export default UsersTable;